<template>
  <div class="cashier-login">
    <div class="login-card">
      <div class="login-title">独立收银台</div>
      <div class="login-sub">请输入账号密码登录</div>
      <a-form layout="vertical" @submit.prevent="handleSubmit">
        <a-form-item>
          <a-input v-model="username" size="large" placeholder="请输入账号">
            <a-icon slot="prefix" type="user" />
          </a-input>
        </a-form-item>
        <a-form-item>
          <a-input-password v-model="password" size="large" placeholder="请输入密码" />
        </a-form-item>
        <a-form-item v-if="checkcodeFlag === '1'">
          <div class="captcha-row">
            <a-input v-model="inputCode" size="large" placeholder="请输入验证码" style="flex: 1; min-width: 0" />
            <img v-if="requestCodeSuccess" class="captcha-img" :src="randCodeImage" title="点击刷新" @click="loadCaptcha" />
            <div v-else class="captcha-fallback" title="点击刷新" @click="loadCaptcha">刷新</div>
          </div>
        </a-form-item>
        <a-button type="primary" size="large" block html-type="submit" :loading="loginBtn">登 录</a-button>
      </a-form>
    </div>
  </div>
</template>

<script>
import md5 from 'md5'
import Vue from 'vue'
import { getAction } from '@/api/manage'
import { queryPermissionsByUser } from '@/api/api'
import { USER_ID, ACCESS_TOKEN } from '@/store/mutation-types'
import { storeMy } from '@/api/plugin/dining'

// 零售收银（040103）与餐饮收银（2906）菜单 url，登录后按此权限分流
const RETAIL_MENU_URL = '/plugin/member/cashier'
const DINING_MENU_URL = '/plugin/dining/cashier-entry'
const LOGIN_FAIL_TIP = {
  'user is not exist': '用户不存在',
  'user password error': '用户密码不正确',
  'user is black': '用户被禁用',
  'tenant is black': '用户所属的租户被禁用',
  'tenant is expire': '试用期已结束，请联系客服续费',
  'access service error': '查询服务异常'
}

/**
 * 独立收银端登录页（全屏，免框架壳）：
 * 登录成功 → storeMy 取所属门店存 sessionStorage('diningStore') → 扫描菜单权限分流：
 * 双权限 → /cashier/choose；仅零售 → /cashier/retail；仅餐饮 → /cashier/dining；双无 → 提示并登出停留
 */
export default {
  name: 'DiningCashierLogin',
  data() {
    return {
      username: '',
      password: '',
      inputCode: '',
      checkcodeFlag: '',
      uuid: '',
      randCodeImage: '',
      requestCodeSuccess: false,
      loginBtn: false
    }
  },
  created() {
    this.loadCheckcodeFlag()
  },
  methods: {
    // 平台配置开启验证码时才展示验证码（照 user/Login.vue 的 checkcodeFlag 开关逻辑，未开启则不传 code/uuid）
    loadCheckcodeFlag() {
      getAction('/platformConfig/getPlatform/checkcodeFlag').then(res => {
        this.checkcodeFlag = res + ''
        if (this.checkcodeFlag === '1') {
          this.loadCaptcha()
        }
      })
    },
    loadCaptcha() {
      getAction('/user/randomImage').then(res => {
        if (res.code === 200) {
          this.uuid = res.data.uuid
          this.randCodeImage = res.data.base64
          this.requestCodeSuccess = true
        } else {
          this.requestCodeSuccess = false
        }
      }).catch(() => {
        this.requestCodeSuccess = false
      })
    },
    handleSubmit() {
      if (this.loginBtn) {
        return
      }
      if (!this.username || !this.password) {
        this.$message.warning('请输入账号和密码')
        return
      }
      if (this.checkcodeFlag === '1' && !this.inputCode) {
        this.$message.warning('请输入验证码')
        return
      }
      this.loginBtn = true
      // 参数名与 user/Login.vue 一致：loginName / password(md5) / code / uuid
      const loginParams = { loginName: this.username, password: md5(this.password) }
      if (this.checkcodeFlag === '1') {
        loginParams.code = this.inputCode
        loginParams.uuid = this.uuid
      }
      // dispatch Login：照 user.js 存 USER_ID/USER_LOGIN_NAME/ACCESS_TOKEN/USER_INFO（Vue.ls 7 天有效）
      this.$store.dispatch('Login', loginParams).then(res => {
        if (res.code === 200 && res.data && res.data.msgTip === 'user can login') {
          this.afterLogin()
        } else {
          this.loginFailed(res)
        }
      }).catch(err => {
        this.loginFailed(err)
      })
    },
    loginFailed(res) {
      const msgTip = res && res.data && res.data.msgTip
      const msg = LOGIN_FAIL_TIP[msgTip] || (res && res.message) || '登录失败，请稍后重试'
      this.$message.error(msg)
      this.inputCode = ''
      if (this.checkcodeFlag === '1') {
        this.loadCaptcha()
      }
      this.loginBtn = false
      this.$store.dispatch('Logout')
    },
    // 登录成功：先取所属门店存 sessionStorage，再按菜单权限分流
    afterLogin() {
      storeMy().then(res => {
        const store = res && res.code === 200 ? res.data : null
        sessionStorage.setItem('diningStore', JSON.stringify(store))
      }).catch(() => {
        sessionStorage.setItem('diningStore', 'null')
      }).finally(() => {
        this.decideEntry()
      })
    },
    /**
     * 权限分流：直调菜单接口（/function/findMenuByPNumber）扫描叶子 url。
     * 不预先 dispatch GetPermissionList：提前填充 permissionList 会导致 permission.js
     * beforeEach 跳过 UpdateAppRouter+addRoutes（照 user/Login.vue decideEntryPath 的注释口径）
     */
    decideEntry() {
      queryPermissionsByUser({ pNumber: 0, userId: Vue.ls.get(USER_ID) }).then(menuData => {
        const urls = this.collectLeafUrls(menuData)
        const hasRetail = urls.some(u => u.indexOf(RETAIL_MENU_URL) > -1)
        const hasDining = urls.some(u => u.indexOf(DINING_MENU_URL) > -1)
        if (hasRetail && hasDining) {
          this.$router.push('/cashier/choose')
        } else if (hasRetail) {
          this.$router.push('/cashier/retail')
        } else if (hasDining) {
          this.$router.push('/cashier/dining')
        } else {
          this.$message.warning('当前账号无收银台权限，请联系管理员分配')
          this.loginBtn = false
          this.$store.dispatch('Logout')
          Vue.ls.remove(ACCESS_TOKEN)
        }
      }).catch(() => {
        this.$message.error('获取菜单权限失败，请重试')
        this.loginBtn = false
      })
    },
    collectLeafUrls(menuData) {
      const urls = []
      const walk = list => {
        ;(list || []).forEach(m => {
          if (m.children && m.children.length > 0) {
            walk(m.children)
          } else if (m.url) {
            urls.push(m.url)
          }
        })
      }
      walk(menuData)
      return urls
    }
  }
}
</script>

<style scoped>
.cashier-login {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10224e 0%, #1d39c4 70%, #2f54eb 100%);
}
.login-card {
  width: 380px;
  background: #fff;
  border-radius: 10px;
  padding: 36px 32px 28px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
}
.login-title {
  font-size: 24px;
  font-weight: 600;
  color: #222;
  text-align: center;
}
.login-sub {
  font-size: 13px;
  color: #999;
  text-align: center;
  margin: 6px 0 24px;
}
.captcha-row {
  display: flex;
  align-items: center;
}
.captcha-img {
  height: 40px;
  margin-left: 8px;
  cursor: pointer;
  border-radius: 4px;
}
.captcha-fallback {
  height: 40px;
  min-width: 90px;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #999;
  cursor: pointer;
  border-radius: 4px;
  font-size: 13px;
}
</style>
